import { forwardRef, useCallback, useEffect, useState } from 'react';
import useTableData from './hooks/useTableData';
import TableBody from './TableBody';
import HorizontalScrollContextProvider from '../../context/HorizontalScrollContextProvider';
import ResultsEmptyState from './components/ResultsEmptyState';
import MultiMemberTableHead from './components/table-head/MultiMemberTableHead';
import { SingleMemberTableHead } from './components/table-head/SingleMemberTableHead';
import classNames from 'classnames';
import { useResultsDataContext } from '../../context/ResultsDataContext';
import { NodeWithoutDescription } from '../../../../../api/table-of-content';
import { useFilters } from '../../context/filter-context/FilterContext';
import { Result } from '../../../../../utils/results';

interface Props {
  showDetailView: boolean;
  hasNoMembers: boolean;
  results: Result[];
  toc: NodeWithoutDescription[];
  onClearFilter: () => void;
}

const ResultsTable = forwardRef<HTMLElement, Props>(function ResultsTable(
  { showDetailView, hasNoMembers, results, toc, onClearFilter },
  container,
) {
  const { scorableItemsPerSchoolYearPerModule } = useResultsDataContext();
  const { selectedMembers, selectedModule, selectedSchoolYear, selectedGroups } = useFilters();
  const [tableEl, setTableEl] = useState<HTMLTableElement | null>(null);

  const determineHorizontalScroll = useCallback(() => {
    if (!tableEl || !(container && 'current' in container && container.current)) {
      return false;
    }

    const { width: tableWidth } = tableEl.getBoundingClientRect();
    const { width: containerWidth } = container.current.getBoundingClientRect();

    return tableWidth > containerWidth;
  }, [container, tableEl]);

  const [horizontalScroll, setHorizontalScroll] = useState(determineHorizontalScroll());

  useEffect(() => {
    if (!tableEl || !selectedGroups.length) return;

    const membersToCount = selectedMembers[selectedGroups[0].id] || [];

    tableEl.style.setProperty('--student-column-count', membersToCount.length.toString());

    setHorizontalScroll(determineHorizontalScroll());
  }, [selectedMembers, determineHorizontalScroll, tableEl, selectedGroups.length, selectedGroups]);

  const {
    allScorableItemsColumn,
    filteredScorableItemsColumn,
    getResultForScorableMaterial,
    getResultForTocNode,
    hasResults,
  } = useTableData({ results, toc });

  if (!selectedModule) return null;

  const hasScorableItemsForSelectedModule =
    scorableItemsPerSchoolYearPerModule[selectedSchoolYear?.id || '']?.[selectedModule?.id || '']?.length > 0;

  if (hasNoMembers) {
    return <ResultsEmptyState variant="no-groups" />;
  } else if (!hasScorableItemsForSelectedModule) {
    return <ResultsEmptyState variant="no-scorable-items" moduleNiceName={selectedModule.niceName} />;
  } else if (!hasResults) {
    return <ResultsEmptyState variant="no-scorable-items-filter" onClear={onClearFilter} />;
  }

  return (
    <HorizontalScrollContextProvider horizontalScroll={horizontalScroll}>
      <table
        className={classNames('results-table', {
          multiple: !showDetailView,
          single: showDetailView,
        })}
        ref={el => setTableEl(el)}
      >
        {!showDetailView ? (
          <MultiMemberTableHead nodeRowGroups={allScorableItemsColumn} />
        ) : (
          <SingleMemberTableHead nodeRowGroups={allScorableItemsColumn} />
        )}
        {filteredScorableItemsColumn.map(node => (
          <TableBody
            key={node.id}
            node={node}
            getResultForTocNode={getResultForTocNode}
            getResultForScorableMaterial={getResultForScorableMaterial}
            showDetailView={showDetailView}
          />
        ))}
      </table>
    </HorizontalScrollContextProvider>
  );
});

export default ResultsTable;
